import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useDeviceProgress } from "./useDeviceProgress";

export const LESSON_NOTE_LIMIT = 2000;
export function useLessonNotes() {
    const { user } = useAuth();
    const { data: notes, update, ready, retry, syncStatus } = useDeviceProgress<Record<string, string>>("lessonNotes");
    const [saving, setSaving] = useState(false);
    const busy = useRef(false);
    const generation = useRef(0);
    useEffect(() => {
        generation.current++;
        busy.current = false;
        setSaving(false);
        // Retire note-save replies when this account changes or unmounts.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        return () => { generation.current++; };
    }, [user?.identityId]);
    const setNote = useCallback(async (key: string, text: string): Promise<boolean> => {
        if (!ready || busy.current || text.length > LESSON_NOTE_LIMIT) return false;
        busy.current = true; setSaving(true);
        const run = generation.current;
        try {
            const value = text.trim();
            const saved = await update(value ? { kind: "set", key, value } : { kind: "remove", key });
            return saved && run === generation.current;
        } finally {
            if (run === generation.current) { busy.current = false; setSaving(false); }
        }
    }, [ready, update]);
    return { notes, ready, saving, loadError: syncStatus === "error", retry, setNote, syncStatus };
}
